// aiFeedbackUtils.js — helpers for AI reply vote snapshots

import { MESSAGE_TYPES } from 'spaces/constants/aiConstants';

/** Admin review workflow statuses for `_ai_feedback` docs. */
export const AI_FEEDBACK_STATUS = {
  NEW: 'new',
  FIXED: 'fixed',
  AS_DESIGNED: 'as_designed',
  ARCHIVED: 'archived',
};

export const AI_FEEDBACK_STATUS_OPTIONS = [
  { value: AI_FEEDBACK_STATUS.NEW, label: 'New' },
  { value: AI_FEEDBACK_STATUS.FIXED, label: 'Fixed' },
  { value: AI_FEEDBACK_STATUS.AS_DESIGNED, label: 'As Designed' },
  { value: AI_FEEDBACK_STATUS.ARCHIVED, label: 'Archived' },
];

const AI_FEEDBACK_STATUS_VALUES = new Set(
  AI_FEEDBACK_STATUS_OPTIONS.map((option) => option.value),
);

/**
 * Normalizes a feedback status; missing/unknown values default to New.
 */
export function normalizeAiFeedbackStatus(value) {
  const cleaned = String(value || '').trim();
  if (AI_FEEDBACK_STATUS_VALUES.has(cleaned)) return cleaned;
  return AI_FEEDBACK_STATUS.NEW;
}

/**
 * Returns the display label for a feedback status.
 */
export function aiFeedbackStatusLabel(value) {
  const normalized = normalizeAiFeedbackStatus(value);
  return AI_FEEDBACK_STATUS_OPTIONS.find((option) => option.value === normalized)?.label
    || 'New';
}

export function isAiMessage(message) {
  const messageType = String(message?.messageType || '').trim();
  if (String(message?.authorKind || '').trim() === 'orbital_ai') return true;
  return messageType === MESSAGE_TYPES.AI_TEXT
    || messageType === MESSAGE_TYPES.AI_SURFACE
    || messageType === MESSAGE_TYPES.AI_FEED_ITEM;
}

/**
 * Finds the nearest preceding user prompt text for an AI reply in a message list.
 */
export function resolvePrecedingPromptText(messages, index) {
  const list = Array.isArray(messages) ? messages : [];
  const current = list[index];
  if (!current) return '';

  const threadRootKey = String(current.threadRootKey || '').trim();

  for (let i = index - 1; i >= 0; i -= 1) {
    const candidate = list[i];
    if (!candidate || isAiMessage(candidate)) continue;

    if (threadRootKey) {
      const candidateKey = String(candidate.key || '').trim();
      const candidateRoot = String(candidate.threadRootKey || '').trim();
      if (candidateKey !== threadRootKey && candidateRoot !== threadRootKey) {
        continue;
      }
    }

    const text = String(candidate.text || '').trim();
    if (text) return text;
  }

  return '';
}

/**
 * Snapshot text for the AI reply being voted on.
 */
export function resolveAiReplyText(message) {
  const text = String(message?.text || '').trim();
  if (text) return text;

  const messageType = String(message?.messageType || '').trim();
  if (messageType === MESSAGE_TYPES.AI_SURFACE) return 'Shared a view';
  if (messageType === MESSAGE_TYPES.AI_FEED_ITEM) {
    const preview = String(message?.feed?.messagePreview || '').trim();
    return preview || 'Base update';
  }

  return '';
}
